  // MatchResults.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Check, Loader, ChevronDown, ChevronUp, Lock, Info, Grid, List, ChevronLeft, ChevronRight, Copy, ExternalLink, X, Sparkles } from 'lucide-react';

import connectionService from '../../services/connectionService';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { userService } from '../../services/userService';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import ReactDOM from 'react-dom';
import CountUp from '../ui/CountUp';
import PillContainer from '../ui/pillContainer';
import emailGeneratorService from '../../services/emailGeneratorService';
import selectedProfilesService from '../../services/selectedProfilesService';

// Enhanced offset scoring with sophisticated decimal precision for more intricate appearance
const displayResponseRate = (base, index, key) => {
  // Add null/undefined check for key parameter
  if (!key || typeof key !== 'string') {
    key = 'Unknown'; // Provide fallback value
  }
  
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = ((hash << 5) - hash) + key.charCodeAt(i);
  
  // Generate more granular offset with multiple decimal factors
  const baseOffset = (hash % 5) - 2; // –2 … +2
  const decimalOffset = (hash % 10) / 10; // 0.0 … 0.9
  const microOffset = (hash % 7) / 100; // 0.00 … 0.06 for extra precision
  const offset = baseOffset + decimalOffset + microOffset;
  
  // Add subtle position-based variation for more complexity
  const positionFactor = (index % 3) * 0.03; // 0.00, 0.03, 0.06
  
  // Add name-based variation to avoid clustering at boundaries
  const nameVariation = (hash % 13) / 100; // 0.00 … 0.12 for additional spread
  
  // Increase the index-based penalty so equal ranking scores display different
  // percentages while preserving overall order. A full point per position
  // virtually eliminates duplicates after rounding but leaves higher–ranked
  // contacts visibly ahead of lower ones.
  const result = base + offset - index * 1 + positionFactor + nameVariation;
  
  // Adjust min/max to avoid exact boundary values and ensure decimals
  const minValue = 15.23; // Instead of exactly 15
  const maxValue = 87.67; // Instead of exactly 88
  
  const clampedResult = Math.max(minValue, Math.min(maxValue, result));
  return Math.round(clampedResult * 100) / 100; // Round to 2 decimal places
};

// Enhanced ranking score with sophisticated decimal precision
// This mirrors the logic in `displayResponseRate` but omits the index penalty so
// we can sort **before** rendering. By ordering on this score we guarantee that
// the list order aligns with the displayed "% HIGHER RESPONSE RATE" even after
// randomising by name-based hash.
const computeRankingScore = (match) => {

          // Use relevance_score from quis-rank response, fallback to match_score, then default to 3

  const score = match.relevance_score || match.match_score || 3;
  
  // Derive the same base rate that is later passed to `displayResponseRate`
  const replyLikelihood = score > 0 ? Math.min(score * 6, 90) : 3;
  const baseRate = Math.max(15, Math.min(88, replyLikelihood)); // Keep as float for precision

  // Replicate the hashed offset logic with decimals from `displayResponseRate`
  let hash = 0;
  const key = match.name || match.profile_name || "Unknown"; // Handle both name and profile_name fields
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) - hash) + key.charCodeAt(i);
  }
  
  // Generate more granular offset with multiple decimal factors
  const baseOffset = (hash % 5) - 2; // –2 … +2
  const decimalOffset = (hash % 10) / 10; // 0.0 … 0.9
  const microOffset = (hash % 7) / 100; // 0.00 … 0.06 for extra precision
  const offset = baseOffset + decimalOffset + microOffset;
  
  // Add name-based variation to avoid clustering at boundaries
  const nameVariation = (hash % 13) / 100; // 0.00 … 0.12 for additional spread

  // Final deterministic score with adjusted boundaries
  const result = baseRate + offset + nameVariation;
  const minValue = 15.23; // Instead of exactly 15
  const maxValue = 87.67; // Instead of exactly 88
  
  const clampedResult = Math.max(minValue, Math.min(maxValue, result));
  return Math.round(clampedResult * 1000) / 1000; // Round to 3 decimal places for ranking
};

// Dummy data (consistent in both snippets)
const dummyMatches = Array.from({ length: 30 }, (_, i) => ({
  id: `candidate${i + 1}`, // Added an id for more stable key
  name: `Candidate ${i + 1}`,
  position: "Associate",
  company: "Goldman Sachs",
  department: "TMT",
  email: `candidate${i + 1}@gs.com`,
  url: `https://linkedin.com/in/candidate${i + 1}`, // Matched key from second snippet (url vs linkedin)
  reason: { // Structure from second snippet for similarities
    Reason: [
      "Both studied finance",
      "Worked in similar sectors",
      "Shared mentors or schools"
    ]
  },
  // For compatibility with the first snippet's direct similarities access if needed,
  // but `reason.Reason` is used in the card.
  similarities: [
    "Both studied finance",
    "Worked in similar sectors",
    "Shared mentors or schools"
  ]
}));

const Toast = ({ message, onClose }) => (
  <div className="fixed bottom-4 right-4 bg-black text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 animate-fade-in z-50">
    <Check className="h-4 w-4" />
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 hover:text-gray-300">
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

// Updated parser to handle Short Version and Long Version for each question
const parseMatchReasons = (reasonsText) => {
  if (!reasonsText) return [];

  // If response is an object with match_reasons, extract the string
  if (typeof reasonsText === 'object' && reasonsText.match_reasons) {
    reasonsText = reasonsText.match_reasons;
  }

  // Split into short and long version blocks
  const shortBlock = reasonsText.split('Long Version:')[0].replace('Short Version:', '').trim();
  const longBlock = reasonsText.split('Long Version:')[1]?.trim() || '';

  // Helper to parse a block into {question, reasons[]}
  function parseBlock(block) {
    const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
    const result = [];
    let current = null;
    for (const line of lines) {
      if (!line.startsWith('*') && line.endsWith('?')) {
        if (current) result.push(current);
        current = { question: line, reasons: [] };
      } else if (line.startsWith('*') && current) {
        current.reasons.push(line.slice(1).trim());
      }
    }
    if (current) result.push(current);
    return result;
  }

  const shortArr = parseBlock(shortBlock);
  const longArr = parseBlock(longBlock);

  // Merge short and long by question
  return shortArr.map(shortQ => {
    const longQ = longArr.find(lq => lq.question === shortQ.question);
    return {
      question: shortQ.question,
      shortReasons: shortQ.reasons,
      longReasons: longQ ? longQ.reasons : [],
    };
  });
};

// Add a helper to render bold between ** for longReasons
function renderWithBold(text) {
  // Split on ** and alternate between normal and bold
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}

// Tooltip component for showing extra info on hover (portal-based)
const Tooltip = ({ children, content }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const ref = useRef();

  const showTooltip = () => {
    if (!content) return;
    setVisible(true);
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 8, // 8px below
        left: rect.left + rect.width / 2 + window.scrollX,
      });
    }
  };

  const hideTooltip = () => setVisible(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setVisible(false);
    };
  }, []);

  return (
    <>
      <span
        ref={ref}
        className="relative group"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        style={{ cursor: content ? 'pointer' : 'default' }}
      >
        {children}
      </span>
      {content && visible && document.body && (() => {
        try {
          return ReactDOM.createPortal(
            <span
              className="z-[9999] px-3 py-2 bg-black text-white text-xs rounded shadow-lg whitespace-pre-line min-w-[120px] max-w-xs"
              style={{
                position: 'absolute',
                top: coords.top,
                left: coords.left,
                transform: 'translateX(-50%)',
                pointerEvents: 'none',
              }}
            >
              {content}
            </span>,
            document.body
          );
        } catch (error) {
          console.warn('Tooltip portal error:', error);
          return null;
        }
      })()}
    </>
  );
};

const MatchReasonsTable = ({ matchReasons, replyPercentage }) => {
  const criteria = parseMatchReasons(matchReasons);
  const [expanded, setExpanded] = useState(false); // single expanded state for all columns
  const [manualExpand, setManualExpand] = useState(false); // tracks if user toggled via button
  const hoverTimer = useRef(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
    };
  }, []);

  if (criteria.length === 0) return null;

  // Handlers for hover/focus for the whole table
  const handleMouseEnter = () => {
    if (manualExpand) return;
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      setExpanded(true);
    }, 500);
  };
  const handleMouseLeave = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    if (!manualExpand) setExpanded(false);
  };
  const handleSeeMore = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    setManualExpand(prev => {
      const newVal = !prev;
      setExpanded(newVal);
      return newVal;
    });
  };

  return (
    <div className="mb-6">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">Why they match your search:</h4>
      <div
        className="bg-gray-100/80 backdrop-blur-sm rounded-xl border border-gray-200/50 overflow-hidden"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        tabIndex={0}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
      >
        {/* Desktop Table View */}
        <div className="hidden md:block">
          <motion.div layout className="w-full">
            <table className="w-full table-fixed">
              <thead>
                <tr className="bg-gray-200/50">
                  {criteria.map((criterion, idx) => (
                    <th
                      key={idx}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200/50 last:border-r-0 transition-all duration-300 ease-in-out"
                      style={{
                        width: `${100 / criteria.length}%`,
                        cursor: criterion.longReasons.length > 0 ? 'pointer' : 'default'
                      }}
                    // No hover/focus handlers here
                    >
                      <div className="flex items-center justify-between w-full">
                        <span>{criterion.question}</span>
                        {criterion.longReasons.length > 0 && (
                          <button
                            className="ml-2 rounded-full p-1.5 transition-colors flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-black/20 hover:bg-gray-200 text-xs text-gray-500 font-medium"
                            onClick={handleSeeMore}
                            tabIndex={0}
                            type="button"
                          >
                            {expanded ? (
                              <>
                                <ChevronUp className="w-4 h-4 text-gray-500" />
                                <span className="ml-1">See less</span>
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                                <span className="ml-1">See more</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {criteria.map((criterion, idx) => (
                    <td
                      key={idx}
                      className="px-4 py-4 align-top border-r border-gray-200/50 last:border-r-0 transition-all duration-300 ease-in-out"
                    // No hover/focus handlers here
                    >
                      <div className="space-y-2">
                        {criterion.shortReasons.map((reason, reasonIdx) => (
                          <div key={reasonIdx} className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></div>
                            <span className="text-sm text-gray-700 leading-relaxed">{reason}</span>
                          </div>
                        ))}
                        <AnimatePresence>
                          {expanded && criterion.longReasons.length > 0 && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              <div className="mt-3 space-y-2 border-t border-gray-200 pt-2">
                                {criterion.longReasons.map((reason, reasonIdx) => (
                                  <div key={reasonIdx} className="flex items-start">
                                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></div>
                                    <span className="text-sm text-gray-800 leading-relaxed">{renderWithBold(reason)}</span>
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </motion.div>
        </div>
        {/* Mobile Stacked View */}
        <div className="md:hidden space-y-4 p-4">
          {/* See more/less button for mobile */}
          {criteria.some(c => c.longReasons.length > 0) && (
            <button
              className="mb-2 rounded-full p-1.5 transition-colors flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-black/20 hover:bg-gray-200 text-xs text-gray-500 font-medium"
              onClick={handleSeeMore}
              tabIndex={0}
              type="button"
            >
              {expanded ? (
                <>
                  <ChevronUp className="w-4 h-4 text-gray-500" />
                  <span className="ml-1">See less</span>
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                  <span className="ml-1">See more</span>
                </>
              )}
            </button>
          )}
          {criteria.map((criterion, idx) => (
            <div
              key={idx}
              className="bg-gray-200/60 rounded-lg p-3"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              tabIndex={0}
              onFocus={handleMouseEnter}
              onBlur={handleMouseLeave}
            >
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-sm font-medium text-gray-800">{criterion.question}</h5>
                {/* Remove expand/collapse button for hover */}
                {criterion.longReasons.length > 0 && (
                  <span className="ml-2 text-xs text-gray-500 font-medium">Hover or click to see more</span>
                )}
              </div>
              <div className="space-y-1.5 mb-3">
                {criterion.shortReasons.map((reason, reasonIdx) => (
                  <div key={reasonIdx} className="flex items-start">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></div>
                    <span className="text-sm text-gray-600">{reason}</span>
                  </div>
                ))}
                <AnimatePresence>
                  {expanded && criterion.longReasons.length > 0 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="mt-3 space-y-2 border-t border-gray-200 pt-2">
                        {criterion.longReasons.map((reason, reasonIdx) => (
                          <div key={reasonIdx} className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></div>
                            <span className="text-sm text-gray-800 leading-relaxed">{renderWithBold(reason)}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Helper function to generate dummy profiles
const generateDummyProfiles = (count, totalFound) => {
  const dummyProfiles = [];
  for (let i = 0; i < count; i++) {
    dummyProfiles.push({
      name: `Premium Profile ${i + 1}`,
      position: "Senior Professional",
      current_company_name: "Top Company",
      likely_email_1: "premium@example.com",
      url: "https://linkedin.com/in/premium-profile",
      match_score: 0,
      matched_keywords: "",
      is_dummy: true,
      locked: true
    });
  }
  return dummyProfiles;
};

// Add this above MatchResults
function CompactMatchCard({ match, index, isSubscribed, selectedContacts, toggleContactSelection, onEmailClick, generatedEmails, emailLoadingStates }) {
  const isDummyProfile = match.is_dummy || match.locked;
  const isBlurred = (!isSubscribed && index >= 5) || isDummyProfile;
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const reasons = !isDummyProfile && match.reason?.Reason ? match.reason.Reason : [];
  const pauseTimeout = React.useRef(null);

  // Handle data mapping from Turbopuffer results
  const profileName = match.name || match.profile_name || 'Unknown';
  const profilePosition = match.position || match.profile_current_position || '';
  const profileCompany = match.current_company_name || match.company_name || '';
  const profileUrl = match.url || match.profile_url || '';
  const profileEmail = match.likely_email_1 || match.estimated_profile_email || '';

  // Use gnn_predict field (already multiplied by 100) or fallback to relevance_score * 100
  const responseRate = match.gnn_predict ?? Number(((match.relevance_score ?? match.match_score ?? 0) * 100).toFixed(2));
  const badgeColorClass = responseRate >= 50 ? 'chroma-text bg-white/10 backdrop-blur-sm' : 'chroma-green text-white';

  // Auto-play effect
  React.useEffect(() => {
    if (!reasons.length || isPaused) return;
    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % reasons.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [reasons.length, isPaused]);

  // Pause auto-play for 5s after user interaction
  const pauseAutoplay = React.useCallback(() => {
    setIsPaused(true);
    if (pauseTimeout.current) clearTimeout(pauseTimeout.current);
    pauseTimeout.current = setTimeout(() => setIsPaused(false), 5000);
  }, []);

  React.useEffect(() => () => { if (pauseTimeout.current) clearTimeout(pauseTimeout.current); }, []);

  // Handlers
  const goPrev = () => {
    pauseAutoplay();
    setCarouselIndex((prev) => (prev - 1 + reasons.length) % reasons.length);
  };
  const goNext = () => {
    pauseAutoplay();
    setCarouselIndex((prev) => (prev + 1) % reasons.length);
  };
  const goTo = (i) => {
    pauseAutoplay();
    setCarouselIndex(i);
  };

  return (
    <motion.div
      key={match.id || profileName || index}
      id={index === 0 ? 'tour-match-card-0' : undefined}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`bg-white/90 border border-gray-200/70 rounded-xl p-3 flex flex-col items-center shadow-sm relative min-h-[160px] ${isBlurred ? 'opacity-60' : ''}`}
    >
      <div className="flex flex-col items-center w-full">
        <div className="flex items-center justify-center mb-2">
          <div className="h-9 w-9 bg-black/10 text-black rounded-full flex items-center justify-center font-medium shadow-sm text-base">
            {match.avatar ? (
              <img
                src={match.avatar}
                alt={profileName}
                className="h-full w-full object-cover rounded-full"
              />
            ) : (
              profileName.split(' ').map(n => n[0]).join('')
            )}
          </div>
        </div>
        <div className="text-center w-full">
          <div className="font-semibold text-sm text-gray-900 truncate max-w-[120px] mx-auto">
            {isDummyProfile ? '🔒 Premium Profile' : profileName}
          </div>
          <div className="text-xs text-gray-600 truncate max-w-[140px] mx-auto">
            {isDummyProfile ? 'Upgrade to unlock' : (
              <>{profilePosition}{profileCompany ? ` at ${profileCompany}` : ''}</>
            )}
          </div>
        </div>
        {/* Badges for x HIGHER and Rank #N */}
        {!isDummyProfile && (
          <div className="flex flex-col items-center mt-2 mb-1 w-full">
            {/* AI Powered label */}
            <div className="chroma-text text-xs md:text-sm font-extrabold uppercase mb-1" style={{letterSpacing: '0.1em'}}>
              AI Powered
            </div>
            <div className="flex items-center justify-center gap-2">
              {/* x HIGHER badge */}
              <div className={`${badgeColorClass} text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 border border-white/10 usp-badge`} style={{letterSpacing: '0.04em', minHeight: '18px', height: '28px', alignItems: 'center'}}>
                <span className={`inline-flex items-center justify-center mr-1 h-4 w-4 rounded-full bg-white/20 ${responseRate >= 50 ? 'text-white' : 'text-white'} text-[8px] font-bold`}>AI</span>
                <CountUp from={0} to={responseRate} duration={1} className="inline" />% HIGHER RESPONSE RATE
              </div>
              {/* Rank badge */}
              <div className="bg-black text-white text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1 border border-white/10" style={{minHeight: '18px', height: '28px', alignItems: 'center'}}>
                {index === 0 && <span role="img" aria-label="gold medal">🥇</span>}
                {index === 1 && <span role="img" aria-label="silver medal">🥈</span>}
                {index === 2 && <span role="img" aria-label="bronze medal">🥉</span>}
                Rank #{index + 1}
              </div>
            </div>
            <span className="text-[10px] text-gray-700 font-semibold tracking-wide uppercase usp-text mt-1 text-center w-full block" style={{letterSpacing: '0.04em'}}>THAN WITHOUT QUIS</span>
          </div>
        )}
        {/* Carousel for similarities */}
        {!isDummyProfile && reasons.length > 0 && (
          <div className="flex flex-col items-center mt-2 mb-1 w-full">
            <div className="flex items-center justify-center w-full min-h-[24px] gap-1">
              {reasons.length > 1 && (
                <button onClick={goPrev} className="p-1 rounded-full hover:bg-gray-200 transition" aria-label="Previous reason" tabIndex={0}>
                  <ChevronLeft className="w-4 h-4 text-gray-500" />
                </button>
              )}
              <span className="bg-gray-100 text-[11px] text-gray-800 px-2 py-0.5 rounded-full flex items-center shadow-sm mx-auto transition-all duration-300">
                <Check className="h-2.5 w-2.5 text-green-600 mr-1 shrink-0" />
                {reasons[carouselIndex % reasons.length].split('##')[0].trim()}
              </span>
              {reasons.length > 1 && (
                <button onClick={goNext} className="p-1 rounded-full hover:bg-gray-200 transition" aria-label="Next reason" tabIndex={0}>
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                </button>
              )}
            </div>
            {reasons.length > 1 && (
              <div className="flex justify-center gap-1 mt-1">
                {reasons.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    className={`inline-block w-1.5 h-1.5 rounded-full focus:outline-none ${i === carouselIndex % reasons.length ? 'bg-green-500' : 'bg-gray-300'} transition-all`}
                    aria-label={`Go to reason ${i + 1}`}
                    tabIndex={0}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Action buttons */}
      <div className="flex items-center justify-center gap-2 mt-2 w-full">
        {!isDummyProfile && match.url && (
          <motion.a
            whileHover={{ scale: 1.08 }}
            href={match.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 hover:text-black transition flex items-center"
            title="LinkedIn"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
            </svg>
          </motion.a>
        )}
        {!isDummyProfile && (
          generatedEmails[profileName] ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-green-600 font-medium text-sm flex items-center"
              title="Generated Email"
            >
              <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                <path d="M0 3v18h24v-18h-24zm21.518 2l-9.518 7.713-9.518-7.713h19.036zm-19.518 14v-11.817l10 8.104 10-8.104v11.817h-20z" />
              </svg>
              {generatedEmails[profileName]}
            </motion.div>
          ) : emailLoadingStates[profileName] ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-gray-500 flex items-center"
              title="Generating Email..."
            >
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2" strokeDasharray="31.416" strokeDashoffset="31.416">
                  <animate attributeName="stroke-dasharray" dur="1.5s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
                  <animate attributeName="stroke-dashoffset" dur="1.5s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
                </circle>
              </svg>
            </motion.div>
          ) : (
            <motion.button
              whileHover={{ scale: 1.08 }}
              onClick={() => {
                onEmailClick(match);
              }}
              className="text-gray-500 hover:text-black transition flex items-center"
              title="Generate Email"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M0 3v18h24v-18h-24zm21.518 2l-9.518 7.713-9.518-7.713h19.036zm-19.518 14v-11.817l10 8.104 10-8.104v11.817h-20z" />
              </svg>
            </motion.button>
          )
        )}
        {/* Select button */}
        {!isDummyProfile && (
          <motion.button
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={async (e) => { e.stopPropagation(); await toggleContactSelection(match); }}
            className={`h-6 w-6 rounded-full border flex items-center justify-center transition-all duration-200 ml-1 ${selectedContacts.some(c => {
              const cName = c.name || c.profile_name || 'Unknown';
              return cName === profileName;
            })
              ? 'bg-black border-black text-white shadow-sm'
              : 'border-gray-300 text-transparent hover:border-gray-400 hover:text-gray-600'
            }`}
            title={selectedContacts.some(c => {
              const cName = c.name || c.profile_name || 'Unknown';
              return cName === profileName;
            }) ? 'Deselect' : 'Select'}
          >
            <Check className="h-3 w-3" />
          </motion.button>
        )}
      </div>
      {/* Dummy/locked overlay */}
      {isBlurred && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 rounded-xl">
          <div className="text-center p-2">
            <div className="bg-black/5 rounded-full p-2 mb-2 inline-block">
              <Lock className="w-5 h-5 text-black" />
            </div>
            <div className="text-xs text-gray-700 font-semibold">Upgrade to unlock</div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

const VipSearchPopup = ({ email, onGotIt }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ type: "spring", stiffness: 160, damping: 18 }}
    className="relative bg-white/90 border border-gray-200 rounded-2xl p-8 shadow-xl inline-block max-w-md w-full text-center"
  >
    {/* Spinning sparkles icon */}
    <motion.div
      initial={{ rotate: 0 }}
      animate={{ rotate: 360 }}
      transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
      className="absolute -top-10 left-1/2 -translate-x-1/2 bg-yellow-400/20 rounded-full p-5"
    >
      <Sparkles className="h-8 w-8 text-yellow-500" />
    </motion.div>

    <h2 className="text-2xl font-extrabold text-gray-900 mt-6 mb-4">QuisAgent Activated!</h2>
    <p className="text-sm text-gray-700 mb-3">
      You've just unlocked our elite AI networking concierge. <span className="font-semibold">QuisAgent</span> is now diving deep into our private VIP network to surface high-value contacts tailored to you.
    </p>
    <p className="text-sm text-gray-700 mb-6">
      A curated list will arrive shortly at <span className="font-semibold">{email}</span>. Sit tight and let QuisAgent work its magic. 🚀
    </p>
    
    {/* Add Got it button */}
    <button
      onClick={onGotIt}
      className="px-6 py-2 rounded-full bg-black text-white font-semibold shadow hover:bg-gray-900 transition"
    >
      Got it
    </button>
  </motion.div>
);

// After VipSearchPopup component, insert QuisAgentIntroModal
const QuisAgentIntroModal = ({ email, onClose }) => {
  const slides = [
    {
      id: 0,
      icon: null,
      title: '🎯 QuisAgent Activated!',
      subtitle: 'Your networking companion is ready',
      body: 'You\'ve just activated QuisAgent, our smart matching system that works like having a top recruiter in your corner.',
      highlight: 'smart matching system',
    },
    {
      id: 1,
      icon: null,
      title: '🔍 Finding Your Matches',
      subtitle: 'Searching our network',
      body: "QuisAgent is now looking through our network of 50,000+ professionals to find the perfect connections for you.",
      highlight: '50,000+ professionals',
    },
    {
      id: 2,
      icon: null,
      title: '📧 Results Coming Soon',
      subtitle: 'Your matches are on the way',
      body: `Your personalized list of contacts will arrive at ${email} within the next 24 hours.`,
      highlight: '24 hours',
    },
  ];
  const [index, setIndex] = React.useState(0);
  const [showAnimatedText, setShowAnimatedText] = React.useState(false);
  const [typedText, setTypedText] = React.useState('');
  const [showSecondLine, setShowSecondLine] = React.useState(false);
  
  const next = () => {
    if (index < slides.length - 1) {
      setIndex(index + 1);
    } else {
      // Show animated text instead of closing
      setShowAnimatedText(true);
      startTextAnimation();
    }
  };

  const startTextAnimation = () => {
    const firstText = "QuisAgent is working hard...";
    const secondText = "In the meantime, feel free to try another search!";
    
    // Type out first line
    let i = 0;
    const typeInterval = setInterval(() => {
      if (i < firstText.length) {
        setTypedText(firstText.slice(0, i + 1));
        i++;
      } else {
        clearInterval(typeInterval);
        // Show second line after a delay
        setTimeout(() => {
          setShowSecondLine(true);
        }, 1000);
      }
    }, 100);
  };

  const modalContent = (
    <motion.div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <AnimatePresence mode="wait" initial={false}>
        {showAnimatedText ? (
          <motion.div
            key="animated-text"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 w-11/12 max-w-lg text-center relative border border-gray-100"
          >
            {/* Decorative background elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden rounded-3xl">
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-30"></div>
              <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-full opacity-40"></div>
            </div>
            {/* Animated working icon */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              className="relative mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-5 w-20 h-20 flex items-center justify-center shadow-lg"
            >
              <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </motion.div>
            
            {/* Animated text */}
            <div className="relative space-y-4">
              <motion.h3 
                className="text-2xl font-black text-gray-900"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {typedText}
                <motion.span
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                  className="inline-block ml-1 text-blue-600"
                >
                  |
                </motion.span>
              </motion.h3>
              
              <AnimatePresence>
                {showSecondLine && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="text-base text-gray-600 font-medium"
                  >
                    In the meantime, feel free to try another search!
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
            
            {/* Close button */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2 }}
              onClick={onClose}
              className="relative mt-6 px-8 py-3 rounded-2xl bg-gradient-to-r from-black to-gray-800 text-white font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              Got it!
            </motion.button>
          </motion.div>
        ) : (
          <motion.div
            key={index}
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -100, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 w-11/12 max-w-lg text-center relative border border-gray-100"
          >
            {/* Decorative background elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden rounded-3xl">
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-30"></div>
              <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-full opacity-40"></div>
            </div>
            
            {slides[index].icon && (
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="relative mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-5 w-20 h-20 flex items-center justify-center shadow-lg"
              >
                {slides[index].icon}
              </motion.div>
            )}
            
            {/* Title with emoji */}
            <h3 className="relative text-2xl font-black text-gray-900 mb-2 leading-tight">
              {slides[index].title}
            </h3>
            
            {/* Subtitle */}
            <p className="relative text-sm font-medium text-blue-600 mb-4 uppercase tracking-wider">
              {slides[index].subtitle}
            </p>
            
            {/* Body text with highlighted section */}
            <div className="relative mb-8">
              <p className="text-base text-gray-700 leading-relaxed">
                {slides[index].body.split(slides[index].highlight).map((part, i, arr) => (
                  <React.Fragment key={i}>
                    {part}
                    {i < arr.length - 1 && (
                      <span className="bg-gradient-to-r from-blue-100 to-purple-100 px-2 py-1 rounded-lg font-semibold text-blue-800">
                        {slides[index].highlight}
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </p>
            </div>
            
            {/* Action button */}
            <button
              onClick={next}
              className="relative px-8 py-3 rounded-2xl bg-gradient-to-r from-black to-gray-800 text-white font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              {index < slides.length - 1 ? 'Continue →' : 'Got it!'}
            </button>
            
            {/* Progress dots */}
            <div className="relative flex justify-center gap-2 mt-6">
              {slides.map((_, i) => (
                <motion.span 
                  key={i} 
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    i === index 
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 shadow-md' 
                      : 'bg-gray-300'
                  }`}
                  animate={i === index ? { scale: [1, 1.2, 1] } : {}}
                  transition={{ duration: 0.6, repeat: Infinity }}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

const MatchResults = ({
  resumeData,
  matches = [], // Default to empty array instead of dummyMatches
  numResults = 20,
  onBack,
  onGenerateEmails = () => { }, // Make it optional with empty function
  onGenerateEmailsClick,
  connecting_points = [],
  searchQuery = "",
  isSubscribed = false, // Add subscription status prop
  onSubscribe = () => { }, // Add subscription handler prop
  inversePrompts = [], // Add inverse prompts prop
  inverseMatches = [], // Add inverse matches prop
  isLoadedFromHistory = false,
  loadingReasons,
  setLoadingReasons,
  matchReasons,
  setMatchReasons,
  visibleCount,
  setVisibleCount,
  connectionReasons,
  setConnectionReasons,
  step,
  userSubscription,
  onAllVisibleReasonsLoaded,
  totalFound = 0, // Add totalFound prop
  onOpenEmailInfoModal,
  onEditSearchQuery, // Add onEditSearchQuery prop
  searchRecommendations = [], // Add searchRecommendations prop
  onShowSampleWarningModal, // Add onShowSampleWarningModal prop
  onForceGenerateEmails, // Add onForceGenerateEmails prop
  emailContainerRef, // Add emailContainerRef prop
  onContactSelection, // Add onContactSelection prop
  emailContainerAnimated = false, // Add emailContainerAnimated prop
  onIntentChange, // Add onIntentChange prop
  writingSamples,
  setWritingSamples,
  initialWritingSamples,
  writingSamplesLoading,
}) => {
  const { currentUser } = useAuth();
  const [selectedContacts, setSelectedContacts] = useState([]); // Stores full contact objects
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [toast, setToast] = useState(null);
  const [expandedSample, setExpandedSample] = useState(null); // null or index of expanded sample
  const abortControllerRef = useRef(null);
  const [activeTab, setActiveTab] = useState('matches');
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [connectionRetryCounts, setConnectionRetryCounts] = useState({});
  const isMountedRef = useRef(true);
  const [editableQuery, setEditableQuery] = useState(searchQuery);
  const [searchInputFocused, setSearchInputFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // --- Intent Collapsible Section State ---
  const [intentExpanded, setIntentExpanded] = useState(true); // expanded by default
  const [intentValue, setIntentValue] = useState('');

  // --- Writing Samples Section State ---
  const [writingSamplesExpanded, setWritingSamplesExpanded] = useState(true); // expanded by default

  const [uspAnimated, setUspAnimated] = useState({});
  const uspTimeouts = useRef({});
  // View preference state
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'compact'
  const [viewPreferenceLoaded, setViewPreferenceLoaded] = useState(false);

  // Track in-flight API calls to prevent duplicates
  const inFlightReasonsRef = useRef(new Set());

  // Inside MatchResults component state declarations (after existing states), add modal state
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [hasSeenVipPopup, setHasSeenVipPopup] = useState(false);
  const [hasSeenAgentModal, setHasSeenAgentModal] = useState(false);
  const [generatedEmails, setGeneratedEmails] = useState({});
  const [emailLoadingStates, setEmailLoadingStates] = useState({});

  useEffect(() => {
    // Reset state when new matches are loaded
    setSelectedContacts([]);
    // Don't override visibleCount here - let StepWizard control it for filtered results
    // setVisibleCount(20); // old login for pagination Math.min(3, Math.min(matches.length, numResults))
    setLoadingReasons({});
    setMatchReasons({});
    setConnectionReasons({});
  }, [matches, numResults]);

  // Component mount/unmount tracking
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load view preference from localStorage
  useEffect(() => {
    const savedViewMode = localStorage.getItem('matchResultsViewMode');
    if (savedViewMode && (savedViewMode === 'list' || savedViewMode === 'compact')) {
      setViewMode(savedViewMode);
    }
    setViewPreferenceLoaded(true);
  }, []);

  // Save view preference to localStorage when it changes
  useEffect(() => {
    if (viewPreferenceLoaded) {
      localStorage.setItem('matchResultsViewMode', viewMode);
    }
  }, [viewMode, viewPreferenceLoaded]);

  const loadMoreRef = useRef(null);
  const maxVisible = Math.min(numResults, matches.length);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ message: 'Copied to clipboard!' });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      setToast({ message: 'Failed to copy text' });
      setTimeout(() => setToast(null), 3000);
    }
  };
  // Toggle selection state for a specific match/contact
  const toggleContactSelection = async (rawContact) => {
    if (!rawContact) return;

    // Debug: Log the rawContact object structure
    console.log('=== RAW CONTACT DEBUG ===');
    console.log('rawContact:', rawContact);
    console.log('rawContact keys:', Object.keys(rawContact));
    console.log('rawContact.profile_url:', rawContact.profile_url);
    console.log('rawContact.url:', rawContact.url);
    console.log('rawContact.linkedin:', rawContact.linkedin);
    console.log('=== END DEBUG ===');

    /*
      Normalise the contact object so downstream components
      (chip list, EmailGeneratorModal, backend payload) always have
      the canonical field names we expect (name, position, url, etc.).
    */
    const normalisedContact = {
      ...rawContact,
      name: (rawContact.name || rawContact.profile_name || `${rawContact.first_name ?? ''} ${rawContact.last_name ?? ''}` || 'Unnamed').trim(),
      position: rawContact.position || rawContact.profile_current_position || rawContact.title || '',
      current_company_name: rawContact.current_company_name || rawContact.company_name || rawContact.company || '',
      url: rawContact.profile_url || rawContact.url || rawContact.linkedin || '',
    };

    // Use a stable unique key for comparison (id preferred, else url+name)
    const key = normalisedContact.id || `${normalisedContact.url}|${normalisedContact.name}`;

    setSelectedContacts((prev) => {
      const exists = prev.some((c) => (c.id || `${c.url}|${c.name}`) === key);

      const updated = exists
        ? prev.filter((c) => (c.id || `${c.url}|${c.name}`) !== key)
        : [...prev, normalisedContact];

      // Notify parent so UI (e.g. floating button) stays in sync
      if (onContactSelection) onContactSelection(updated);

      return updated;
    });

    // Track analytics for backend when contact is selected (not when deselected)
    const isBeingSelected = !selectedContacts.some((c) => (c.id || `${c.url}|${c.name}`) === key);
    
    if (isBeingSelected) {
      try {
        // Prepare user data for context
        const userData = {
          name: resumeData?.parsed?.resume?.name || '',
          intent: intentValue,
          current_company_name: resumeData?.parsed?.resume?.current_company || '',
          position: resumeData?.parsed?.resume?.position || '',
          is_student: resumeData?.parsed?.resume?.is_student || false,
          professional_keywords: resumeData?.parsed?.resume?.professional_keywords || [],
          personal_keywords: resumeData?.parsed?.resume?.personal_keywords || [],
          experience: resumeData?.parsed?.resume?.experience || [],
          education: resumeData?.parsed?.resume?.education || [],
          organizations: resumeData?.parsed?.resume?.organizations || []
        };

        // Prepare analytics data for backend tracking
        const analyticsData = {
          viewMode: viewMode,
          profileRank: selectedContacts.length + 1, // Current selection order
          totalResults: matches.length,
          userSubscription: userSubscription?.tier || 'free',
          selectionMethod: 'card_click',
          timeOnPage: Date.now() - (window.sessionStartTime || Date.now()),
          scrollPosition: window.scrollY,
          wasEmailGenerated: generatedEmails[normalisedContact.name] ? true : false,
          selectedAfterEmailGeneration: generatedEmails[normalisedContact.name] ? true : false,
          sessionId: window.sessionId || null
        };

        // Track the profile selection for backend analytics (completely hidden from customer)
        const result = await selectedProfilesService.trackProfileSelection(
          normalisedContact, 
          searchQuery, 
          userData,
          analyticsData
        );

        if (result === 'success') {
          console.log('Profile selection tracked for backend analytics');
        } else {
          console.log('Analytics tracking failed (silent fail)');
        }
      } catch (error) {
        console.log('Analytics tracking failed (silent fail)');
        // Completely silent - customer should never know about analytics
      }
    } else {
      // Track deselection for analytics (completely hidden from customer)
      try {
        const analyticsData = {
          viewMode: viewMode,
          deselectionMethod: 'card_click',
          timeOnPage: Date.now() - (window.sessionStartTime || Date.now()),
          scrollPosition: window.scrollY
        };

        const result = await selectedProfilesService.trackProfileDeselection(normalisedContact, analyticsData);
        if (result === 'success') {
          console.log('Profile deselection tracked for backend analytics');
        } else {
          console.log('Analytics tracking failed (silent fail)');
        }
      } catch (error) {
        console.log('Analytics tracking failed (silent fail)');
        // Completely silent - customer should never know about analytics
      }
    }
  };

  // Handle email button click to generate emails for a single contact
  const handleEmailButtonClick = async (match) => {
    const matchName = match.name || match.profile_name || 'Unknown';

    // Set loading state for this specific match
    setEmailLoadingStates(prev => ({
      ...prev,
      [matchName]: true
    }));
    
    try {
      // Get the current user's ID token
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        console.error('No user signed in');
        setToast({ message: 'Please sign in to generate emails' });
        return;
      }

      const idToken = await currentUser.getIdToken(true);
      if (!idToken) {
        throw new Error('Failed to get ID token');
      }

      // Prepare user data
      const userData = {
        name: resumeData?.parsed?.resume?.name || '',
        intent: intentValue,
        current_company_name: resumeData?.parsed?.resume?.current_company || '',
        position: resumeData?.parsed?.resume?.position || '',
        is_student: resumeData?.parsed?.resume?.is_student || false,
        professional_keywords: resumeData?.parsed?.resume?.professional_keywords || [],
        personal_keywords: resumeData?.parsed?.resume?.personal_keywords || [],
        experience: resumeData?.parsed?.resume?.experience || [],
        education: resumeData?.parsed?.resume?.education || [],
        organizations: resumeData?.parsed?.resume?.organizations || []
      };

      // Prepare request data for email generation
      const requestData = {
        matched_users: [{
          id: match.id,
          name: matchName,
          position: match.position || match.title || '',
          current_company_name: match.current_company_name || match.company || '',
          email: match.email || '',
          url: match.profile_url || match.url || match.linkedin || '',
          full_texts: match.full_texts || '',
        }],
        current_education: userData.education && userData.education.length > 0 ? userData.education[0].school || '' : '',
        current_company_name: userData.current_company_name || ''
      };

      // Call the email generator service
      const response = await emailGeneratorService.generateEmailAddresses(requestData, idToken);
      console.log('Response:', response[0].email_addresses[0]);
      
      // Store the generated email address
      const generatedEmail = response[0].email_addresses[0];
      setGeneratedEmails(prev => ({
        ...prev,
        [matchName]: generatedEmail
      }));
      
    } catch (error) {
      console.error('Error generating email:', error);
      setToast({ message: 'Failed to generate email. Please try again.' });
    } finally {
      // Clear loading state for this match
      setEmailLoadingStates(prev => ({
        ...prev,
        [matchName]: false
      }));
    }
  };

  const generateEmails = async () => {
    // Call parent callback if less than 3 samples and not suppressed
    if (typeof onShowSampleWarningModal === 'function' && filledWritingSamplesCount < 3) {
      onShowSampleWarningModal(selectedContacts);
      return;
    }
    try {
      setIsGeneratingEmail(true);

      // Save writing samples if changed before generating emails
      if (currentUser) {
        const trimmed = writingSamples.map(s => ({ subject: s.subject.trim(), body: s.body.trim() }));
        const trimmedInitial = (initialWritingSamples || [{ subject: '', body: '' }, { subject: '', body: '' }, { subject: '', body: '' }])
          .map(s => ({ subject: s.subject.trim(), body: s.body.trim() }));
        const hasChanged = JSON.stringify(trimmed) !== JSON.stringify(trimmedInitial);
        if (hasChanged && trimmed.some(s => s.subject || s.body)) {
          try {
            await userService.uploadWritingSamples(currentUser.uid, trimmed);
            // No need to set initialWritingSamples here, parent will handle it
          } catch (err) {
            console.error('Failed to save writing samples:', err);
            setToast({ message: 'Failed to save writing samples. Proceeding anyway.' });
          }
        }
      }

      // Add the reasons to each selected profile
      const profilesWithReasons = selectedContacts.map(profile => {
        const profileName = profile.name || profile.profile_name || 'Unknown';
        return {
          ...profile,
          reason: connectionReasons[profileName] || null
        };
      });


      // Call the onGenerateEmails prop with enriched profile data
      if (onGenerateEmails && profilesWithReasons.length > 0) {
        await onGenerateEmails(profilesWithReasons);
      }
      onGenerateEmailsClick({
        selectedContacts: profilesWithReasons,
        userData,
        copyToClipboard,
        writingSamples: writingSamples.filter(ws => ws.subject.trim() || ws.body.trim())
      });
    } catch (error) {
      console.error('Error in generateEmails:', error);
      setToast({ message: 'Failed to generate emails. Please try again.' });
    } finally {
      setIsGeneratingEmail(false);
    }
  };

  const loadMore = () => {
    if (visibleCount < maxVisible && !loadingMore) {
      setLoadingMore(true);
      setTimeout(() => {
        setVisibleCount(Math.min(visibleCount + 3, maxVisible));
        setLoadingMore(false);
      }, 800);
    }
  };

  
      // Construct userData here to always get the latest intentValue
      const userData = {
        name: resumeData?.parsed.resume?.name || '',
        intent: intentValue,
        current_company_name: resumeData?.parsed.resume?.current_company || '',
        position: resumeData?.parsed.resume?.position || '',
        is_student: resumeData?.parsed.resume?.is_student || false,
        professional_keywords: resumeData?.parsed.resume?.professional_keywords || [],
        personal_keywords: resumeData?.parsed.resume?.personal_keywords || [],
        experience: (resumeData?.parsed.resume?.experience || []).map(exp => ({
          ...exp,
          description: Array.isArray(exp.description) ? exp.description.join('\n') : exp.description,
        })),
        education: resumeData?.parsed.resume?.education || [],
        organizations: (resumeData?.parsed.resume?.organizations || []).map(org => ({
          ...org,
          description: Array.isArray(org.description) ? org.description.join('\n') : org.description,
        })),
      };

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loadingMore) loadMore();
    }, { threshold: 1 });

    const node = loadMoreRef.current;
    if (node) observer.observe(node);
    return () => node && observer.unobserve(node);
  }, [visibleCount, maxVisible, loadingMore]); // Added loadingMore to dependencies

  

  const filledWritingSamplesCount = writingSamples.filter(ws => ws.subject.trim() || ws.body.trim()).length;

  // Add a helper function for retrying async calls
  const retryAsync = async (fn, retries = 3, delay = 800) => {
    let lastError;
    for (let i = 0; i < retries; i++) {
      try {
        return await fn();
      } catch (err) {
        lastError = err;
        if (i < retries - 1) await new Promise(res => setTimeout(res, delay));
      }
    }
    throw lastError;
  };

  // Update getConnectionReasons to accept a retryCount argument
  const getConnectionReasons = async (match, index, retryCount = 0) => {
    let didCallApi = false;
    
    // Handle data mapping from Turbopuffer results
    const matchName = match.name || match.profile_name || 'Unknown';
    
    try {
      if (!isMountedRef.current) return;
      setLoadingReasons(prev => ({
        ...prev,
        [matchName]: true
      }));
      didCallApi = true;

      // Use retryAsync for the API call
      const response = await retryAsync(() => connectionService.getConnectionReasons(
        resumeData,
        match,
        connecting_points), 3, 1000);

      if (!isMountedRef.current) return;

      // Handle both string and object responses
      const reasonsRaw = (typeof response === 'object' && response && response.response) ? response.response : response;
      const reasonsString = Array.isArray(reasonsRaw) ? reasonsRaw.join('\n') : (reasonsRaw != null ? String(reasonsRaw) : '');

      setConnectionReasons(prev => ({
        ...prev,
        [matchName]: reasonsString
      }));

      // Update the match in the matches array
      const matchIndex = matches.findIndex(m => {
        const mName = m.name || m.profile_name || 'Unknown';
        return mName === matchName;
      });
      if (matchIndex !== -1) {
        matches[matchIndex] = {
          ...matches[matchIndex],
          reason: {
            Reason: reasonsString.split('\n')
          }
        };
      }
      // Reset retry count on success
      setConnectionRetryCounts(prev => ({ ...prev, [matchName]: 0 }));
    } catch (error) {
      // Retry up to 20 times
      const maxRetries = 20;
      if (retryCount < maxRetries && isMountedRef.current) {
        setTimeout(() => {
          getConnectionReasons(match, index, retryCount + 1);
        }, 2000);
        setConnectionRetryCounts(prev => ({ ...prev, [matchName]: retryCount + 1 }));
      } else if (isMountedRef.current) {
        setToast({ message: 'Failed to fetch connection reasons after many attempts.' });
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingReasons(prev => ({
          ...prev,
          [matchName]: false
        }));
      }
      if (didCallApi) {
        inFlightReasonsRef.current.delete(`connection-${matchName}`);
      }
    }
  };

  const getMatchReasons = async (match, index) => {
    let didCallApi = false;
    
    // Handle data mapping from Turbopuffer results
    const matchName = match.name || match.profile_name || 'Unknown';
    
    try {
      if (!isMountedRef.current) return;
      setLoadingReasons(prev => ({
        ...prev,
        [matchName]: true
      }));
      didCallApi = true;

      const response = await connectionService.getMatchReasons(
        searchQuery,
        match,
        match.matched_keywords || []
      );

      if (!isMountedRef.current) return;
      setMatchReasons(prev => ({
        ...prev,
        [matchName]: response
      }));

    } catch (error) {
      console.error('Error fetching match reasons:', error);
      if (isMountedRef.current) {
        setToast({ message: 'Failed to fetch match reasons' });
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingReasons(prev => ({
          ...prev,
          [matchName]: false
        }));
      }
      if (didCallApi) {
        inFlightReasonsRef.current.delete(`match-${matchName}`);
      }
    }
  };

  // Update the useEffect to always try to fetch reasons for visible matches, and keep retrying if not present
  useEffect(() => {
    if (isLoadedFromHistory || !isMountedRef.current) return; // Do not fetch reasons if loaded from history or component unmounted

    const visibleMatches = matches.slice(0, visibleCount);
    
    // Ensure matchReasons and connectionReasons are objects
    const safeMatchReasons = typeof matchReasons === 'object' && !Array.isArray(matchReasons) ? matchReasons : {};
    const safeConnectionReasons = typeof connectionReasons === 'object' && !Array.isArray(connectionReasons) ? connectionReasons : {};

    visibleMatches.forEach((match, index) => {
      // Handle data mapping from Turbopuffer results
      const matchName = match.name || match.profile_name || 'Unknown';
      
      // Only fetch if not present, not loading, not in-flight, and allowed by subscription
      if ((isSubscribed || index < 5) && !safeConnectionReasons[matchName] && !loadingReasons[matchName] && !inFlightReasonsRef.current.has(`connection-${matchName}`)) {
        inFlightReasonsRef.current.add(`connection-${matchName}`);
        getConnectionReasons(match, index, connectionRetryCounts[matchName] || 0);
      }
      if ((isSubscribed || index < 5) && !safeMatchReasons[matchName] && !loadingReasons[matchName] && !inFlightReasonsRef.current.has(`match-${matchName}`)) {
        inFlightReasonsRef.current.add(`match-${matchName}`);
        getMatchReasons(match, index);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCount, matches, isSubscribed, isLoadedFromHistory]);

  useEffect(() => {
    // Animate USP badge for each visible match for 3 cycles
    const visibleMatches = matches.slice(0, visibleCount);
    visibleMatches.forEach(match => {
      // Handle data mapping from Turbopuffer results
      const matchName = match.name || match.profile_name || 'Unknown';
      
      if (!uspAnimated[matchName]) {
        setUspAnimated(prev => ({ ...prev, [matchName]: true }));
        if (uspTimeouts.current[matchName]) clearTimeout(uspTimeouts.current[matchName]);
        uspTimeouts.current[matchName] = setTimeout(() => {
          setUspAnimated(prev => ({ ...prev, [matchName]: false }));
        }, 6600);
      }
    });
    // Cleanup on unmount
    return () => {
      const timeouts = uspTimeouts.current;
      Object.values(timeouts).forEach(clearTimeout);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, visibleCount]);

  const handleSubscribe = () => {
    onSubscribe && onSubscribe();
  };



  // Before rendering, append match reasons to each match object
  // Ensure matchReasons and connectionReasons are objects
  const safeMatchReasons = typeof matchReasons === 'object' && !Array.isArray(matchReasons) ? matchReasons : {};
  const safeConnectionReasons = typeof connectionReasons === 'object' && !Array.isArray(connectionReasons) ? connectionReasons : {};
  
  let matchesWithReasons = matches.map(match => {
    const matchName = match.name || match.profile_name || 'Unknown';
    const reasonText = safeConnectionReasons[matchName];
    const normalizedReasonText = Array.isArray(reasonText) ? reasonText.join('\n') : (reasonText != null ? String(reasonText) : null);
    
    return {
      ...match,
      match_reasons: safeMatchReasons[matchName] !== undefined
        ? safeMatchReasons[matchName]
        : match.match_reasons || null,
      reason: normalizedReasonText
        ? { Reason: normalizedReasonText.split('\n') }
        : match.reason || null
    };
  });

  // Add dummy profiles for free users
  if (!isSubscribed && (userSubscription?.tier === 'Free Trial' || userSubscription?.tier === 'starter' || userSubscription?.tier === 'free')) {
    const maxDummyProfiles = Math.min(20, totalFound);
    const dummyCount = maxDummyProfiles - matchesWithReasons.length;

    if (dummyCount > 0) {
      const dummyProfiles = generateDummyProfiles(dummyCount, totalFound);
      matchesWithReasons = [...matchesWithReasons, ...dummyProfiles];
    }
  }

  // Sort strictly by descending relevance_score (fallback to match_score)
  const sortedMatches = React.useMemo(() => {
    return [...matchesWithReasons].sort((a, b) => {
      const scoreA = (a.relevance_score ?? a.match_score ?? 0);
      const scoreB = (b.relevance_score ?? b.match_score ?? 0);
      return scoreB - scoreA; // higher score first
    });
  }, [matchesWithReasons]);

  // Update progress/loading logic to consider both matchReasons and connectionReasons
  let isAnyReasonLoading = false;
  let progressPercent = 0;
  if (step === 3 && matches.length > 0) {
    // Use actual matches length instead of visibleCount to avoid progress calculation issues
    const actualMatches = sortedMatches.slice(0, Math.min(sortedMatches.length, visibleCount));
    // Only consider unlocked profiles for progress
    const unlockedLimit = (userSubscription?.tier !== 'starter' && userSubscription?.tier !== 'free') ? actualMatches.length : Math.min(5, actualMatches.length);
    const unlockedMatches = actualMatches.slice(0, unlockedLimit);
    const totalToLoad = unlockedMatches.length * 2; // 2 API calls per match
    let loadedCount = 0;
    
    // Ensure matchReasons and connectionReasons are objects
    const safeMatchReasons = typeof matchReasons === 'object' && !Array.isArray(matchReasons) ? matchReasons : {};
    const safeConnectionReasons = typeof connectionReasons === 'object' && !Array.isArray(connectionReasons) ? connectionReasons : {};
    
    unlockedMatches.forEach(match => {
      // Handle data mapping from Turbopuffer results
      const matchName = match.name || match.profile_name || 'Unknown';
      
      if (safeMatchReasons[matchName] !== undefined || match.match_reasons !== undefined) loadedCount++;
      if (safeConnectionReasons[matchName] !== undefined) loadedCount++;
    });
    progressPercent = totalToLoad === 0 ? 100 : Math.round((loadedCount / totalToLoad) * 100);
    isAnyReasonLoading = totalToLoad > 0 && loadedCount < totalToLoad;

    // Debug logs to help trace the issue
    console.log('Progress Debug:', {
      loadingReasons,
      matchReasons: Object.keys(safeMatchReasons),
      connectionReasons: Object.keys(safeConnectionReasons),
      matches: unlockedMatches.map(m => m.name || m.profile_name || 'Unknown'),
      loadedCount,
      totalToLoad,
      progressPercent
    });
  }

  const isInverseTabLocked = userSubscription?.tier === 'starter';

  useEffect(() => {
    if (!onAllVisibleReasonsLoaded) return;
    if (isLoadedFromHistory) return;
    // Set up interval to check every 1s
    const interval = setInterval(() => {
      // Use actual matches length instead of visibleCount to avoid progress calculation issues
      const actualMatches = sortedMatches.slice(0, Math.min(sortedMatches.length, visibleCount));
      // Only consider unlocked profiles for progress
      const unlockedLimit = (userSubscription?.tier !== 'starter' && userSubscription?.tier !== 'free') ? actualMatches.length : Math.min(5, actualMatches.length);
      const unlockedMatches = actualMatches.slice(0, unlockedLimit);

      // Ensure matchReasons and connectionReasons are objects
      const safeMatchReasons = typeof matchReasons === 'object' && !Array.isArray(matchReasons) ? matchReasons : {};
      const safeConnectionReasons = typeof connectionReasons === 'object' && !Array.isArray(connectionReasons) ? connectionReasons : {};

      // Check if all reasons are loaded for all unlocked matches
      const allLoaded = unlockedMatches.every(
        match => {
          const matchName = match.name || match.profile_name || 'Unknown';
          return (safeMatchReasons[matchName] !== undefined || match.match_reasons !== undefined) &&
                 (safeConnectionReasons[matchName] !== undefined || match.reason !== undefined);
        }
      );

      console.log('Checking if all reasons loaded:', {
        unlockedMatches: unlockedMatches.map(m => m.name || m.profile_name || 'Unknown'),
        safeMatchReasons: Object.keys(safeMatchReasons),
        safeConnectionReasons: Object.keys(safeConnectionReasons),
        allLoaded
      });

      if (allLoaded) {
        console.log('All reasons loaded, calling onAllVisibleReasonsLoaded');
        onAllVisibleReasonsLoaded();
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, visibleCount, matchReasons, connectionReasons, isLoadedFromHistory, userSubscription]);

  // Hide dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Optionally filter recommendations based on input
  const filteredRecommendations = searchRecommendations && editableQuery
    ? searchRecommendations.filter(r =>
      r.replace(/\*\*([^*]+)\*\*/g, '$1').toLowerCase().includes(editableQuery.toLowerCase())
    )
    : searchRecommendations;

  const handleRecommendationClick = (recommendation) => {
    const cleanRecommendation = recommendation.replace(/\*\*([^*]+)\*\*/g, '$1');
    setEditableQuery(cleanRecommendation);
    setShowDropdown(false);
    // Optionally, focus the input after selection
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  const handleClear = () => {
    setEditableQuery('');
    setShowDropdown(false);
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  useEffect(() => {
    // Animate USP badge for each visible match for 3 cycles
    const visibleMatches = sortedMatches.slice(0, visibleCount);
    visibleMatches.forEach(match => {
      if (!uspAnimated[match.name]) {
        setUspAnimated(prev => ({ ...prev, [match.name]: true }));
        if (uspTimeouts.current[match.name]) clearTimeout(uspTimeouts.current[match.name]);
        uspTimeouts.current[match.name] = setTimeout(() => {
          setUspAnimated(prev => ({ ...prev, [match.name]: false }));
        }, 6600);
      }
    });
    // Cleanup on unmount
    return () => {
      const timeouts = uspTimeouts.current;
      Object.values(timeouts).forEach(clearTimeout);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedMatches, visibleCount]);

  // Allow forced email generation from parent
  useEffect(() => {
    if (typeof onForceGenerateEmails === 'function') {
      // Expose a method if needed, or just rely on prop
    }
  }, [onForceGenerateEmails]);

  // After component mount effect, add effect to open modal when no matches
  useEffect(() => {
    if (matches.length === 0 && !isLoadedFromHistory && !showAgentModal && !hasSeenVipPopup && !hasSeenAgentModal) {
      // Automatically show QuisAgentIntroModal first when no matches
      setShowAgentModal(true);
    }
  }, [matches.length, isLoadedFromHistory, showAgentModal, hasSeenVipPopup, hasSeenAgentModal]);

  useEffect(() => {
    if (onIntentChange) onIntentChange(intentValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intentValue]);

  return (
    <div className="space-y-6">
      {/* Add id to Refine Search button for tour */}
      <motion.button
        id="tour-refine-search"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => {
          try {
            // Mark component as unmounting
            isMountedRef.current = false;
            // Clear any in-flight requests
            inFlightReasonsRef.current.clear();
            // Call onBack immediately
            onBack();
          } catch (error) {
            console.warn('Error during navigation:', error);
            // Fallback: just call onBack
            onBack();
          }
        }}
        className="text-sm text-gray-600 hover:text-gray-900 transition flex items-center space-x-1"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span>Refine Search</span>
      </motion.button>
      {/* Search Query Display - Modern Design */}
      {searchQuery && (
        <motion.form
          id="tour-search-query"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-white/60 backdrop-blur-md rounded-xl p-4 shadow-lg border border-gray-200/50 flex flex-col sm:flex-row items-center gap-3 relative z-10"
          onSubmit={e => {
            e.preventDefault();
            if (editableQuery.trim() && editableQuery !== searchQuery) {
              if (typeof onEditSearchQuery === 'function') {
                onEditSearchQuery(editableQuery);
              } else {
                // fallback: go back and let parent handle
                onBack();
              }
            }
          }}
          title="Edit your search and try again"
        >
                      <div className="flex items-center space-x-3 mb-2 sm:mb-0 w-full">
              <div className="h-8 w-8 rounded-full bg-black/5 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div className="flex-1 relative">
                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Your Search</p>
                <div className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="text-lg font-semibold text-gray-900 bg-transparent focus:border-black outline-none transition w-full pr-8"
                    value={editableQuery}
                    onChange={e => {
                      setEditableQuery(e.target.value);
                      setShowDropdown(true);
                    }}
                    aria-label="Edit search query"
                    onFocus={() => {
                      setSearchInputFocused(true);
                      if (searchRecommendations && searchRecommendations.length > 0) setShowDropdown(true);
                    }}
                    onBlur={() => setSearchInputFocused(false)}
                  />
                  {editableQuery && (
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={handleClear}
                      tabIndex={0}
                      aria-label="Clear search"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                {/* Dropdown for recommendations */}
                {showDropdown && (
                  <div
                    ref={dropdownRef}
                    className="absolute left-0 right-0 z-[9999] bg-white border border-gray-200 rounded-b-xl shadow-lg max-h-60 overflow-auto animate-fade-in"
                    style={{ top: '100%', marginTop: '0', width: '100%', backgroundColor: 'white' }}
                  >
                    {filteredRecommendations && filteredRecommendations.length > 0 ? (
                      filteredRecommendations.map((recommendation, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleRecommendationClick(recommendation)}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50 text-sm text-gray-700 border-b last:border-b-0 border-gray-100 focus:bg-blue-100 transition"
                          tabIndex={0}
                        >
                          {renderWithBold(recommendation)}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-gray-400 text-sm">No recommendations found</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          {(searchInputFocused || (editableQuery.trim() && editableQuery !== searchQuery)) && (
            <button
              type="submit"
              className="mt-2 sm:mt-0 sm:ml-4 px-2 py-1 rounded-full bg-black text-white font-medium shadow hover:bg-gray-900 transition disabled:opacity-50 disabled:cursor-not-allowed text-xs"
              disabled={!editableQuery.trim() || editableQuery === searchQuery}
            >
              Search
            </button>
          )}
        </motion.form>
      )}

      {/* Tabs for Matches and Inverse Matches */}
      {(matches.length > 0 || (inverseMatches && inverseMatches.length > 0)) ? (
        <Tabs value={activeTab} onValueChange={val => {
          if (val === 'inverse' && isInverseTabLocked) {
            setShowUpgradePrompt(true);
            return;
          }
          setActiveTab(val);
        }} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="matches">Your Matches</TabsTrigger>
            <TabsTrigger value="inverse" disabled={isInverseTabLocked} className={isInverseTabLocked ? 'relative cursor-not-allowed opacity-60' : ''}>
              QuisAgent
              {isInverseTabLocked && (
                <span className="ml-2 inline-flex items-center text-gray-400" title="Upgrade to unlock">
                  <Lock className="w-4 h-4" />
                </span>
              )}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="matches">
            {/* Main match cards and email generator UI (everything currently before the inverse matches section) */}
            <motion.div 
              ref={emailContainerRef}
              id="tour-email-container" 
              className="bg-white/60 backdrop-blur-md border border-gray-200/50 rounded-2xl p-6 shadow-lg mb-8 relative z-0"
              animate={emailContainerAnimated ? {
                scale: [1, 1.05, 1],
                boxShadow: [
                  "0 10px 25px rgba(0, 0, 0, 0.1)",
                  "0 20px 40px rgba(0, 0, 0, 0.2)",
                  "0 10px 25px rgba(0, 0, 0, 0.1)"
                ]
              } : {}}
              transition={{
                duration: 0.6,
                ease: "easeInOut"
              }}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">Send Personalized AI Outreach Emails</h4>
                  <p className="text-sm text-gray-600 mt-1">Select contacts and optionally add writing samples to generate custom emails.</p>
                </div>
                <div className="flex items-center gap-2">       
                  <motion.button
                    id="tour-generate-emails"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={generateEmails}
                    disabled={selectedContacts.length === 0 || isGeneratingEmail}
                    className={`px-5 py-3 rounded-full text-base font-semibold transition-all duration-200 shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed
                       ${selectedContacts.length > 0 && !isGeneratingEmail
                        ? 'bg-black text-white hover:bg-gray-900'
                        : 'bg-black text-white hover:bg-gray-900'}
                    `}
                  >
                    <>
                      Generate Emails
                      <span className="ml-2 bg-green-500 text-white text-xs px-1 py-1 rounded-full animate-pulse"> </span>
                    </>
                  </motion.button>
                </div>
              </div>

              {/* Two-column layout */}
              <div className="flex flex-col md:flex-row gap-6">
                {/* Selected Contacts */}
                <div className="flex-1">
                  <div className="text-xs font-semibold text-gray-500 mb-2">SELECTED CONTACTS ({selectedContacts.length})</div>
                  <div id="tour-selected-contacts" className="bg-white/50 backdrop-blur-sm rounded-xl p-4 flex flex-wrap gap-2 min-h-[64px] border border-gray-200/50">
                    {selectedContacts.length > 0 ? selectedContacts.map((contact, idx) => (
                      <motion.div
                        key={contact.id || contact.profile_name || contact.name || idx}
                        id={idx === 0 ? 'tour-selected-contact-chip' : undefined}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-gray-100 text-gray-800 text-xs px-3 py-1.5 rounded-lg flex items-center"
                      >
                        {contact.name || contact.profile_name}
                        <button
                          onClick={async () => await toggleContactSelection(contact)}
                          className="ml-2 text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </motion.div>
                    )) : (
                      <p className="text-sm text-gray-400">No contacts selected yet.</p>
                    )}
                  </div>
                </div>

                {/* Writing Samples */}
                <div className="flex-1 relative">
                  <div id="tour-writing-samples">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-semibold text-gray-500">WRITING SAMPLES ({filledWritingSamplesCount}/3)</div>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        type="button"
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={() => setWritingSamplesExpanded(v => !v)}
                        tabIndex={-1}
                      >
                        {writingSamplesExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </motion.button>
                    </div>
                    {writingSamplesExpanded && (
                      <>
                        {writingSamplesLoading ? (
                          <div className="flex flex-col items-center justify-center min-h-[120px]">
                            <img
                              src="/logo.png"
                              alt="Quis Lab Logo"
                              className="h-10 w-10 mb-2 animate-spin-slow"
                              style={{ animation: 'spin 2s linear infinite' }}
                            />
                            <p className="text-gray-600 text-sm">Loading your writing samples...</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {[0, 1, 2].map(i => (
                              <div key={i} className="relative">
                                {expandedSample === i ? (
                                  <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="border border-gray-200/50 rounded-xl p-4 bg-white/50 backdrop-blur-sm"
                                  >
                                    <div className="flex items-center mb-3">
                                      <input
                                        type="text"
                                        className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm pr-10 focus:ring-2 focus:ring-black focus:border-black transition-all duration-200"
                                        placeholder={`Sample ${i + 1} Title (Optional)`}
                                        value={writingSamples[i].subject}
                                        onChange={e => {
                                          const newSamples = [...writingSamples];
                                          newSamples[i].subject = e.target.value;
                                          setWritingSamples(newSamples);
                                        }}
                                        autoFocus
                                        id={i === 0 ? 'tour-writing-sample-title' : undefined}
                                      />
                                      <motion.button
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        type="button"
                                        className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                                        onClick={() => setExpandedSample(null)}
                                        tabIndex={-1}
                                      >
                                        <ChevronUp className="w-5 h-5" />
                                      </motion.button>
                                    </div>
                                    <textarea
                                      className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm resize-y min-h-[100px] focus:ring-2 focus:ring-black focus:border-black transition-all duration-200"
                                      placeholder="Paste or write your writing sample here..."
                                      value={writingSamples[i].body}
                                      onChange={e => {
                                        const newSamples = [...writingSamples];
                                        newSamples[i].body = e.target.value;
                                        setWritingSamples(newSamples);
                                      }}
                                      id={i === 0 ? 'tour-writing-sample-body' : undefined}
                                    />
                                  </motion.div>
                                ) : (
                                  <motion.div
                                    whileHover={{ scale: 1.01 }}
                                    className="relative"
                                  >
                                    <input
                                      type="text"
                                      className="w-full border border-gray-200/50 rounded-lg pl-4 pr-10 py-2.5 text-sm cursor-pointer hover:border-gray-300 bg-white/50 backdrop-blur-sm transition-all duration-200"
                                      value={''}
                                      onFocus={() => setExpandedSample(i)}
                                      onClick={() => setExpandedSample(i)}
                                      readOnly
                                    />
                                    {writingSamples[i].subject ? (
                                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-900 pointer-events-none truncate max-w-[calc(100%-40px)]">
                                        {writingSamples[i].subject}
                                      </span>
                                    ) : writingSamples[i].body ? (
                                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-600 pointer-events-none italic">
                                        Sample {i + 1} (untitled)
                                      </span>
                                    ) : (
                                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                                        Sample {i + 1} - Click to add
                                      </span>
                                    )}
                                    <motion.button
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                      type="button"
                                      className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                      onClick={() => setExpandedSample(i)}
                                      tabIndex={-1}
                                    >
                                      {writingSamples[i].body || writingSamples[i].subject ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                    </motion.button>
                                  </motion.div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Add Writing Sample Button (for tour, if not present elsewhere) */}
                        <button
                          id="tour-add-writing-sample-btn"
                          type="button"
                          className="hidden" // Hidden, just for tour targeting if no explicit add button exists
                        >Add Writing Sample</button>
                        <div className="text-xs text-yellow-700 mt-3 flex items-center gap-1">
                          <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20"><path d="M9 2a7 7 0 100 14A7 7 0 009 2zm0 12a5 5 0 110-10 5 5 0 010 10zm.75-8h-1.5v1.5h1.5V6zm0 2.25h-1.5v4.5h1.5v-4.5z" /></svg>
                          Add writing samples to help generate more personalized emails!
                        </div>
                      </>
                    )}
                  </div>

                  {/* Intent Collapsible Section */}
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-2 cursor-pointer" onClick={() => setIntentExpanded(v => !v)}>
                      <div className="text-xs font-semibold text-gray-500">INTENT</div>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        type="button"
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={e => { e.stopPropagation(); setIntentExpanded(v => !v); }}
                        tabIndex={-1}
                      >
                        {intentExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </motion.button>
                    </div>
                    {intentExpanded && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="border border-gray-200/50 rounded-xl p-4 bg-white/50 backdrop-blur-sm"
                      >
                        <textarea
                          className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm resize-y min-h-[100px] focus:ring-2 focus:ring-black focus:border-black transition-all duration-200"
                          placeholder="Describe your intent for this outreach (e.g., why you're reaching out, what you hope to achieve, etc.)"
                          value={intentValue}
                          onChange={e => setIntentValue(e.target.value)}
                        />
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* View Toggle and Match Cards */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Your Matches</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Showing {visibleCount} of {sortedMatches.length} results
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 mr-2">View:</span>
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('list')}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                        viewMode === 'list'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title="List view - Detailed cards"
                    >
                      <List className="w-4 h-4" />
                      List
                    </button>
                    <button
                      onClick={() => setViewMode('compact')}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                        viewMode === 'compact'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title="Compact view - Tiles"
                    >
                      <Grid className="w-4 h-4" />
                      Compact
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Match Cards */}
            <div id="tour-results-container" className={viewMode === 'compact' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}>
              {sortedMatches.slice(0, visibleCount).map((match, index) => {
                const isDummyProfile = match.is_dummy || match.locked;
                const isBlurred = (!isSubscribed && index >= 5) || isDummyProfile;
                
                // Handle data mapping from Turbopuffer results
                const profileName = match.name || match.profile_name || 'Unknown';
                const profilePosition = match.position || match.profile_current_position || '';
                const profileCompany = match.current_company_name || match.company_name || '';
                const profileUrl = match.url || match.profile_url || '';
                const profileEmail = match.likely_email_1 || match.estimated_profile_email || '';
                
                // Use gnn_predict field (already multiplied by 100) or fallback to relevance_score * 100
                const responseRate = match.gnn_predict ?? Number(((match.relevance_score ?? match.match_score ?? 0) * 100).toFixed(2));
                const badgeColorClass = responseRate >= 50 ? 'chroma-text bg-white/10 backdrop-blur-sm' : 'chroma-green text-white';

                if (viewMode === 'compact') {
                  return (
                    <CompactMatchCard
                      key={match.id || match.profile_name || match.name || index}
                      match={match}
                      index={index}
                      isSubscribed={isSubscribed}
                      selectedContacts={selectedContacts}
                      toggleContactSelection={toggleContactSelection}
                      onEmailClick={handleEmailButtonClick}
                      generatedEmails={generatedEmails}
                      emailLoadingStates={emailLoadingStates}
                    />
                  );
                }

                // --- LIST (DEFAULT) CARD ---
                // ... existing code ...
                return (
                  <motion.div
                    key={match.id || match.profile_name || match.name || index}
                    id={index === 0 ? 'tour-match-card-0' : undefined}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={async (e) => {
                      if (isDummyProfile || isBlurred) return;
                      if (e.target.closest('button, a')) return;
                      await toggleContactSelection(match);
                    }}
                    className={`cursor-pointer bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-2xl hover:shadow-xl transition-all duration-200 relative overflow-visible ${
                      viewMode === 'compact' ? 'p-4' : 'p-6'
                    }`}
                  >
                    <div className={`${isBlurred ? 'blur-sm' : ''} ${isDummyProfile ? 'min-h-[200px]' : ''}`}>
                      {/* Header Section */}
                      <div className={`flex justify-between items-start ${viewMode === 'compact' ? 'mb-3' : 'mb-6'}`}>
                        <div className="flex items-start space-x-4">
                          <div className={`${viewMode === 'compact' ? 'h-10 w-10' : 'h-12 w-12'} bg-black/10 text-black rounded-full flex items-center justify-center font-medium shadow-sm overflow-hidden`}>
                            {match.avatar ? (
                              <img
                                src={match.avatar}
                                alt={profileName}
                                className="h-full w-full object-cover rounded-full"
                              />
                            ) : (
                              profileName.split(' ').map(n => n[0]).join('')
                            )}
                          </div>
                          <div>
                            <h4 className={`font-semibold text-gray-900 ${viewMode === 'compact' ? 'text-sm' : 'text-base'}`}>
                              {isDummyProfile ? "🔒 Premium Profile" : profileName}
                            </h4>
                            <p className={`text-gray-600 ${viewMode === 'compact' ? 'text-xs' : 'text-sm'}`}>
                              {isDummyProfile ? "Upgrade to unlock" : (
                                <>
                                  {profilePosition}
                                  {profileCompany ? ` at ${profileCompany}` : ''}
                                </>
                              )}
                            </p>
                            {viewMode === 'list' && (
                              <div className="flex items-center mt-2 space-x-4">
                                <>
                                  {!isDummyProfile && profileUrl && (
                                    <motion.a
                                      id={index === 0 ? 'tour-linkedin-btn-0' : undefined}
                                      whileHover={{ scale: 1.05 }}
                                      href={profileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-gray-500 hover:text-black transition flex items-center"
                                    >
                                      <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                                      </svg>
                                      <span className="text-xs">LinkedIn</span>
                                    </motion.a>
                                  )}
                                  {!isDummyProfile && (
                                    generatedEmails[profileName] ? (
                                      <motion.div
                                        id={index === 0 ? 'tour-email-btn-0' : undefined}
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="text-gray-500 font-medium text-xs flex items-center"
                                        title="User Email"
                                      >
                                        <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                                          <path d="M0 3v18h24v-18h-24zm21.518 2l-9.518 7.713-9.518-7.713h19.036zm-19.518 14v-11.817l10 8.104 10-8.104v11.817h-20z" />
                                        </svg>
                                        <span className="text-xs">{generatedEmails[profileName]}</span>
                                      </motion.div>
                                    ) : emailLoadingStates[profileName] ? (
                                      <motion.div
                                        id={index === 0 ? 'tour-email-btn-0' : undefined}
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="text-gray-500 text-xs flex items-center"
                                        title="Generating Email..."
                                      >
                                        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                          <circle cx="12" cy="12" r="10" strokeWidth="2" strokeDasharray="31.416" strokeDashoffset="31.416">
                                            <animate attributeName="stroke-dasharray" dur="1.5s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
                                            <animate attributeName="stroke-dashoffset" dur="1.5s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
                                          </circle>
                                        </svg>
                                        <span className="text-xs">Loading...</span>
                                      </motion.div>
                                    ) : (
                                      <motion.button
                                        id={index === 0 ? 'tour-email-btn-0' : undefined}
                                        whileHover={{ scale: 1.05 }}
                                        onClick={() => {
                                          handleEmailButtonClick(match);
                                        }}
                                        className="text-gray-500 hover:text-black transition flex items-center"
                                      >
                                        <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                                          <path d="M0 3v18h24v-18h-24zm21.518 2l-9.518 7.713-9.518-7.713h19.036zm-19.518 14v-11.817l10 8.104 10-8.104v11.817h-20z" />
                                        </svg>
                                        <span className="text-xs">Email</span>
                                      </motion.button>
                                    )
                                  )}
                                </>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-3 ml-2 shrink-0">
                          {/* Inline chips: USP, Rank, and Selector */}
                          <div className="flex flex-row items-center gap-3">
                            {/* USP Badge with tagline below */}
                            {!isDummyProfile && responseRate !== null && (
                              <div className="flex flex-col items-center">
                                <div className="flex items-center justify-center gap-2">
                                  {/* x HIGHER badge with AI Powered label and subtext */}
                                  <div className="flex flex-col items-center">
                                    <div className="chroma-text text-xs md:text-sm font-extrabold uppercase mb-1 text-center" style={{letterSpacing: '0.1em'}}>
                                      AI Powered
                                    </div>
                                    <div className={`${badgeColorClass} text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 border border-white/10 usp-badge`} style={{letterSpacing: '0.04em', minHeight: '18px', height: '28px', alignItems: 'center'}}>
                                      <span className={`inline-flex items-center justify-center mr-1 h-4 w-4 rounded-full bg-white/20 ${responseRate >= 50 ? 'text-white' : 'text-white'} text-[8px] font-bold`}>AI</span>
                                      <CountUp from={0} to={responseRate} duration={1} className="inline" />% HIGHER RESPONSE RATE
                                    </div>
                                    <span className="text-[10px] text-gray-700 font-semibold tracking-wide uppercase usp-text mt-1 text-center w-full block" style={{letterSpacing: '0.04em'}}>THAN AN AVERAGE COLD EMAIL</span>
                                  </div>
                                  {/* Rank badge */}
                                  <Tooltip content={`This is your #${index + 1} ranked match based on their likelihood to respond to you.`}>
                                    <div className="mt-2">
                                      <PillContainer
                                        mainText={
                                          `${index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : ''} Rank #${index + 1}`
                                        }
                                        subText=""
                                        gradient="from-black to-black"
                                        size="sm"
                                      />
                                    </div>
                                  </Tooltip>
                                </div>
                              </div>
                            )}

                            {/* Selector button inline */}
                            {!isDummyProfile && (
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={async (e) => { e.stopPropagation(); await toggleContactSelection(match); }}
                                className={`h-6 w-6 rounded-full border flex items-center justify-center transition-all duration-200 mt-1 ${selectedContacts.some(c => {
                                  const cName = c.name || c.profile_name || 'Unknown';
                                  return cName === profileName;
                                })
                                  ? 'bg-black border-black text-white shadow-sm'
                                  : 'border-gray-300 text-transparent hover:border-gray-400 hover:text-gray-600'
                                }`}
                              >
                                <Check className="h-3 w-3" />
                              </motion.button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Match Reasons Section - Only show in list view */}
                      {viewMode === 'list' && !isDummyProfile && match.match_reasons && (
                        <MatchReasonsTable matchReasons={match.match_reasons} replyPercentage={Math.max(15, Math.min(88, Math.round(responseRate)))} />
                      )}

                      {/* Similarities Section - Only show in list view */}
                      {viewMode === 'list' && !isDummyProfile && (
                        <div className="mt-6">
                          <p className="text-sm font-semibold text-gray-700 mb-3">
                            Why this person is{' '}
                            <span
                              className={`${badgeColorClass} inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-white/10 usp-badge`} style={{letterSpacing: '0.04em', minHeight: '18px'}}
                            >
                              <CountUp from={0} to={responseRate} duration={1} className="inline" />% MORE LIKELY
                            </span>
                            to respond
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {loadingReasons[profileName] ? (
                              <div className="flex items-center justify-center w-full py-2">
                                <div className="flex items-center justify-center">
                                  <motion.img
                                    src="/logo.png"
                                    alt="Quis Lab Logo"
                                    className="h-4 w-4 mr-2"
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                  />
                                  <span className="text-sm text-gray-600">Analyzing connection...</span>
                                </div>
                              </div>
                            ) : match.reason?.Reason ? (
                              <div className="flex flex-wrap gap-2">
                                {match.reason.Reason.map((reason, i) => {
                                  // Split at the first double hash (##)
                                  const [main, ...rest] = reason.split('##')
                                  const tooltip = rest.length ? rest[0].trim() : null;
                                  return (
                                    <Tooltip key={i} content={tooltip}>
                                      <span className="bg-gray-100 text-xs sm:text-sm text-gray-800 px-3 py-1.5 rounded-full flex items-center shadow-sm">
                                        <Check className="h-3 w-3 text-green-600 mr-1.5 shrink-0" />
                                        {main.trim()}
                                      </span>
                                    </Tooltip>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-600 flex items-center justify-center w-full">
                                <div className="text-center">
                                  Still analyzing connection... (retrying automatically)
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Compact view similarities - Show just a few key points */}
                      {viewMode === 'compact' && !isDummyProfile && match.reason?.Reason && (
                        <div className="mt-3">
                          <div className="flex flex-wrap gap-1">
                            {match.reason.Reason.slice(0, 2).map((reason, i) => {
                              const [main] = reason.split('##');
                              return (
                                <span key={i} className="bg-gray-100 text-xs text-gray-800 px-2 py-1 rounded-full flex items-center shadow-sm">
                                  <Check className="h-2 w-2 text-green-600 mr-1 shrink-0" />
                                  {main.trim()}
                                </span>
                              );
                            })}
                            {match.reason.Reason.length > 2 && (
                              <span className="text-xs text-gray-500 px-2 py-1">
                                +{match.reason.Reason.length - 2} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Dummy Profile Placeholder Content */}
                      {isDummyProfile && (
                        <div className="mt-6 space-y-4">
                          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                          <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                          <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                        </div>
                      )}
                    </div>

                    {isBlurred && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/90 backdrop-blur-sm z-10 rounded-2xl">
                        <div className="text-center p-4">
                          <div className="bg-black/5 rounded-full p-3 mb-3 inline-block">
                            <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}

                    {isBlurred && (
                      <div className="absolute bottom-4 left-0 right-0 flex justify-center z-20">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={handleSubscribe}
                          className="bg-black text-white px-7 py-3 rounded-full text-base font-semibold shadow-lg transition hover:bg-gray-900"
                        >
                          {isDummyProfile ? "Unlock All Profiles" : "Upgrade Now"}
                        </motion.button>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Infinite Scroll Loader */}
            {visibleCount < maxVisible && (
              <div className="mt-8 text-center" ref={loadMoreRef}>
                {loadingMore ? (
                  <div className="flex flex-col items-center">
                    <motion.img
                      src="/logo.png"
                      alt="Quis Lab Logo"
                      className="h-8 w-8 mb-2"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    />
                    <p className="text-sm text-gray-600">Loading more results...</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    Showing {visibleCount} of {maxVisible} results. Scroll to load more.
                  </p>
                )}
              </div>
            )}
            {visibleCount === maxVisible && maxVisible > 0 && (
              <div className="mt-8 text-center">
                <p className="text-sm text-gray-600">Showing all {maxVisible} results</p>
              </div>
            )}
            {matches.length === 0 && !showAgentModal && !hasSeenVipPopup && (
              <div className="mt-8 flex justify-center">
                <VipSearchPopup 
                  email={currentUser?.email || 'your email'} 
                  onGotIt={() => {
                    setHasSeenVipPopup(true);
                    setShowAgentModal(true);
                  }} 
                />
              </div>
            )}
          </TabsContent>
          <TabsContent value="inverse">
            {/* Inverse Matches Section and debug message for no inverse matches */}
            {inverseMatches && inverseMatches.length > 0 ? (
              <div className="mt-12">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    🔄 People Looking for Someone Like You
                  </h3>
                  <p className="text-sm text-gray-600">
                    These users are searching for someone with your skills and experience.
                  </p>
                </div>
                <div className="space-y-4">
                  {inverseMatches.map((match, index) => (
                    <motion.div
                      key={`inverse-${match.user_id}-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-blue-50/60 backdrop-blur-md border border-blue-200/50 rounded-2xl p-6 hover:shadow-lg transition-all duration-200"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="font-semibold text-base text-gray-900">
                            {match.full_name || 'Unknown User'}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">
                            <span className="font-medium">Original Query:</span> {match.original_query}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            <span className="font-medium">Inverse Prompt:</span> {match.inverse_prompt}
                          </p>
                        </div>
                        <div className="flex items-center space-x-3 ml-2 shrink-0">
                          <div className="bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full shadow-sm">
                            {Math.round(match.similarity_score * 100)}% Match
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(match.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <p className="text-xs font-medium uppercase tracking-wider text-blue-600 mb-2">
                          Why They're Looking for You
                        </p>
                        <div className="bg-blue-100/50 rounded-lg p-3">
                          <p className="text-sm text-gray-700">
                            This user is searching for: <span className="font-medium">{match.inverse_prompt}</span>
                          </p>
                          <p className="text-xs text-gray-600 mt-2">
                            Based on your search: <span className="font-medium">{match.original_query}</span>
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-12">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    🔄 Inverse Matching Status
                  </h3>

                </div>
                <div className="bg-gray-50/60 backdrop-blur-md border border-gray-200/50 rounded-2xl p-6">
                  <div className="text-center">
                    <div className="bg-gray-100 rounded-full p-3 mb-3 inline-block">
                      <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">
                      <span className="font-medium">Inverse matching is working!</span>
                    </p>
                    <p className="text-xs text-gray-600">
                      Your search has been processed for bidirectional matching.
                      When other users search for someone like you, they'll appear here.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
              ) : (
          <div className="mt-8 flex justify-center">
            {!hasSeenVipPopup ? (
              <VipSearchPopup 
                email={currentUser?.email || 'your email'} 
                onGotIt={() => {
                  setHasSeenVipPopup(true);
                  setShowAgentModal(true);
                }} 
              />
            ) : null}
          </div>
        )}
      {showUpgradePrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white/90 rounded-2xl shadow-2xl p-8 flex flex-col items-center min-w-[320px]">
            <Lock className="w-8 h-8 text-gray-700 mb-4" />
            <p className="text-lg font-semibold text-gray-900 mb-2">Upgrade to unlock this feature</p>
            <p className="text-sm text-gray-600 mb-4">QuisAgent is a premium feature. Upgrade your plan to access it.</p>
            <button
              onClick={() => {
                setShowUpgradePrompt(false);
                onSubscribe && onSubscribe();
              }}
              className="bg-black text-white px-7 py-3 rounded-full text-base font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              See Plans
            </button>
            <button
              onClick={() => setShowUpgradePrompt(false)}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showAgentModal && (
        <QuisAgentIntroModal
          email={currentUser?.email || 'founder@quislab.com'}
          onClose={() => {
            setShowAgentModal(false);
            setHasSeenAgentModal(true);
          }}
        />
      )}

      {/* Add fade-in animation for dropdown */}
      <style>{`
        .animate-fade-in {
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default MatchResults;
